import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./css/mypage.css";
import WithdrawalPasswordModal from "./modal/WithdrawalPasswordModal";
import WithdrawalConfirmModal from "./modal/WithdrawalConfirmModal";
import { apiGet } from "../../utils/apiClient";
import { formatDate } from "../../utils/commonService";
import {
  initBillingPayment,
  requestBillingAuth,
  registerBillingKey,
  deleteBillingKey,
} from "../../utils/storeCardRegister";

const MyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [basicActive, setIsBasicActive] = useState(true);
  const [membershipActive, setIsMembershipActive] = useState(false);
  const [cardActive, setIsCardActive] = useState(false);
  const [storeData, setStoreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [billingResult, setBillingResult] = useState(null); // null | "register" | "edit" | "delete"
  const [showBillingFailModal, setShowBillingFailModal] = useState(false);
  const [payment, setPayment] = useState(null);
  const [cardData, setCardData] = useState(null);
  const [showCardDeleteConfirmModal, setShowCardDeleteConfirmModal] = useState(false);
  const billingRegisteredRef = useRef(false);

  const fetchCardData = async () => {
    try {
      const response = await apiGet(`/store/billing`);
      if (!response.ok)
        throw new Error(`Failed to fetch card data: ${response.status}`);
      const data = await response.json();
      setCardData(data);
    } catch (error) {
      console.error("Error fetching card data:", error);
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        let userInfo = localStorage.getItem("user");
        const storeCode = JSON.parse(userInfo).storeCode;

        const response = await apiGet(`/store/view/${storeCode}`);

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Error response:", errorText);
          throw new Error(`Failed to fetch store data: ${response.status}`);
        }

        const data = await response.json();
        setStoreData(data);
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const initPayment = async () => {
      try {
        const p = await initBillingPayment();
        setPayment(p);
      } catch (error) {
        console.error("Toss init error:", error);
      }
    };
    initPayment();
  }, []);

  // 카드 등록 성공 후 리다이렉트 감지
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("tab") === "card") {
      setIsCardActive(true);
      setIsBasicActive(false);
      setIsMembershipActive(false);
      navigate("/store/mypage", { replace: true });
    } else if (params.get("billing") === "success") {
      if (billingRegisteredRef.current) return;
      billingRegisteredRef.current = true;

      const billingMode = params.get("billingMode");
      registerBillingKey(params)
        .then(() => {
          setBillingResult(billingMode === "edit" ? "edit" : "register");
          fetchCardData();
        })
        .catch((error) => {
          console.error("카드 등록 API 오류:", error);
          setShowBillingFailModal(true);
        });

      setIsCardActive(true);
      setIsBasicActive(false);
      setIsMembershipActive(false);
      navigate("/store/mypage", { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (cardActive) {
      fetchCardData();
    }
  }, [cardActive]);

  async function handleCardRegister() {
    if (!payment) return;
    await requestBillingAuth(payment, storeData, false); // false == 등록 모드
  }

  async function handleCardEdit() {
    if (!payment) return;
    await requestBillingAuth(payment, storeData, true); // true == 수정 모드
  }

  async function handleCardDelete() {
    setShowCardDeleteConfirmModal(false);
    await deleteBillingKey();
    setCardData(null);
    setBillingResult("delete");
  }

  return (
    <div className="mypage-wrap">
      <div className="mypage-header">
        <div className="mypage-header-title">마이페이지</div>
      </div>

      {loading ? (
        <div>로딩 중...</div>
      ) : !storeData ? (
        <div>데이터를 불러올 수 없습니다.</div>
      ) : (
        <div className="mypage-top-content-wrap">
          <div className="mypage-top-content-name-wrap">
            <div className="mypage-top-content-name-contain">
              <div className="mypage-top-content-name">{storeData.storeNm}</div>
              <div className="mypage-top-content-confirmtag">
                {storeData.approvalStatus}
              </div>
            </div>
            <div className="mypage-top-content-btn-group">
              <button
                className="mypage-top-content-btn mypage-top-content-edit-btn"
                onClick={() => navigate("/store/edit")}
              >
                수정
              </button>
              <div className="mypage-withdrawal-wrap">
                <button
                  className={`mypage-top-content-btn mypage-top-content-signout-btn${storeData.canWithdrawal ? "" : " mypage-top-content-signout-btn-disabled"}`}
                  onClick={() => storeData.canWithdrawal && setShowPasswordModal(true)}
                  disabled={!storeData.canWithdrawal}
                >
                  탈퇴
                </button>
                {!storeData.canWithdrawal && (
                  <div className="mypage-withdrawal-tooltip">
                    진행중인 할인/네고가 있어 탈퇴가 불가능합니다.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mypage-top-content">
            <div className="mypage-top-content-tab-group">
              <div
                className={
                  basicActive
                    ? "mypage-top-content-tab mypage-top-content-tab-active"
                    : "mypage-top-content-tab"
                }
                onClick={() => {
                  setIsBasicActive(true);
                  setIsMembershipActive(false);
                  setIsCardActive(false);
                }}
              >
                기본 정보
              </div>
              <div
                className={
                  membershipActive
                    ? "mypage-top-content-tab mypage-top-content-tab-active"
                    : "mypage-top-content-tab"
                }
                onClick={() => {
                  setIsMembershipActive(true);
                  setIsBasicActive(false);
                  setIsCardActive(false);
                }}
              >
                멤버쉽 정보
                  </div>
              {/* // TODO: [2026-06-23] 카드 정보 등록 관련 비활성화 - 추후 활성화 */}
              {/* <div
                className={
                  cardActive
                    ? "mypage-top-content-tab mypage-top-content-tab-active"
                    : "mypage-top-content-tab"
                }
                onClick={() => {
                  setIsCardActive(true);
                  setIsBasicActive(false);
                  setIsMembershipActive(false);
                }}
              >
                카드 정보
              </div> */}
            </div>
          </div>

          {basicActive && (
            <div className="mypage-middle-content-wrap">
              <div className="mypage-middle-content-left">
                <div className="mypage-middle-content-align">
                  <div className="mypage-middle-content-title">가맹점 정보</div>

                  <div className="mypage-middle-store-info-wrap">
                    <div className="mypage-middle-store-info-left">
                      <div className="mypage-middle-content-info">
                        <div className="mypage-middle-content-subtitle">
                          가맹점명
                        </div>
                        <div className="mypage-middle-content-subcontent">
                          {storeData.storeNm}
                        </div>
                      </div>
                      <div className="mypage-middle-content-info">
                        <div className="mypage-middle-content-subtitle">
                          전화번호
                        </div>
                        <div className="mypage-middle-content-subcontent">
                          {storeData.storeTelNo}
                        </div>
                      </div>
                      <div className="mypage-middle-content-info">
                        <div className="mypage-middle-content-subtitle">
                          주소
                        </div>
                        <div className="mypage-middle-content-subcontent">
                          {storeData.storeAddr} {storeData.storeAddrDetail}
                        </div>
                      </div>
                      <div className="mypage-middle-content-info">
                        <div className="mypage-middle-content-subtitle">
                          누적 신고횟수
                        </div>
                        <div className="mypage-middle-content-subcontent">
                          {storeData.storeReport}회
                        </div>
                      </div>
                    </div>

                    <div className="mypage-middle-store-info-right">
                      {storeData.mainImg && storeData.mainImg.fileUrl ? (
                        <img
                          src={storeData.mainImg.fileUrl}
                          alt="대표 이미지"
                          className="mypage-middle-store-image"/>
                      ) : (
                        <div className="mypage-middle-store-no-image">
                          이미지 없음
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mypage-middle-content-align">
                  <div className="mypage-middle-content-title">계좌 정보</div>

                  <div className="mypage-middle-content-info">
                    <div className="mypage-middle-content-subtitle">은행명</div>
                    <div className="mypage-middle-content-subcontent">
                      {storeData.bankNm}
                    </div>
                  </div>
                  <div className="mypage-middle-content-info">
                    <div className="mypage-middle-content-subtitle">
                      계좌번호
                    </div>
                    <div className="mypage-middle-content-subcontent">
                      {storeData.storeAccNo}
                    </div>
                  </div>
                  <div className="mypage-middle-content-info">
                    <div className="mypage-middle-content-subtitle">예금주</div>
                    <div className="mypage-middle-content-subcontent">
                      {storeData.ownerNm}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mypage-middle-content-right">
                <div className="mypage-middle-content-align">
                  <div className="mypage-middle-content-title">사업자 정보</div>

                  <div className="mypage-middle-content-owner-wrap">
                    <div className="mypage-middle-content-info">
                      <div className="mypage-middle-content-subtitle">
                        사업자명
                      </div>
                      <div className="mypage-middle-content-subcontent">
                        {storeData.bizNm}
                      </div>
                    </div>
                    <div className="mypage-middle-content-info">
                      <div className="mypage-middle-content-subtitle">
                        사업자번호
                      </div>
                      <div className="mypage-middle-content-subcontent">
                        {storeData.bizNo}
                      </div>
                    </div>
                    <div className="mypage-middle-content-info">
                      <div className="mypage-middle-content-subtitle">대표</div>
                      <div className="mypage-middle-content-subcontent">
                        {storeData.ownerNm}
                      </div>
                    </div>
                    <div className="mypage-middle-content-info">
                      <div className="mypage-middle-content-subtitle">
                        대표 연락처
                      </div>
                      <div className="mypage-middle-content-subcontent">
                        {storeData.ownerTelNo}
                      </div>
                    </div>
                    <div className="mypage-middle-content-info">
                      <div className="mypage-middle-content-subtitle">
                        가맹점 유형
                      </div>
                      <div className="mypage-middle-content-subcontent">
                        {storeData.storeType}
                      </div>
                    </div>
                    <div className="mypage-middle-content-info">
                      <div className="mypage-middle-content-subtitle">
                        사업자 등록일
                      </div>
                      <div className="mypage-middle-content-subcontent">
                        {storeData.requestedDateTime
                          ? formatDate(storeData.requestedDateTime)
                          : "-"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mypage-middle-content-align">
                  <div className="mypage-middle-content-title">운영 정보</div>
                  <div className="mypage-middle-content-info"></div>
                  <div className="mypage-middle-content-info">
                    <div className="mypage-middle-content-subtitle">
                      오픈시간
                    </div>
                    <div className="mypage-middle-content-subcontent">
                      {storeData.storeOpenTime} ~ {storeData.storeCloseTime}
                    </div>
                  </div>
                  <div className="mypage-middle-content-info">
                    <div className="mypage-middle-content-subtitle">영업일</div>
                    <div className="mypage-middle-content-subcontent">
                      월 ~ 금요일
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {membershipActive && (
            <div className="mypage-middle-content-wrap-m">
              <div className="mypage-middle-content-up">
                <div className="mypage-middle-content-align-m">
                  <div className="mypage-middle-content-title-m">
                    멤버쉽 정보
                  </div>
                  <div className="mypage-middle-content-up-align">
                    <div className="mypage-middle-content-info">
                      <div className="mypage-middle-content-subtitle">
                        활성 멤버쉽 정보
                      </div>
                      <div className="mypage-middle-content-subcontent">
                        {storeData.planNm}
                      </div>
                    </div>
                    <div className="mypage-middle-content-info">
                      <div className="mypage-middle-content-subtitle">
                        구독 시작일
                      </div>
                      <div className="mypage-middle-content-subcontent">
                        {storeData.membershipStartDate
                          ? formatDate(storeData.membershipStartDate)
                          : "-"}
                      </div>
                    </div>
                    <div className="mypage-middle-content-info">
                      <div className="mypage-middle-content-subtitle">
                        다음 결제일
                      </div>
                      <div className="mypage-middle-content-subcontent">
                        {storeData.nextBillingDate
                          ? formatDate(storeData.nextBillingDate)
                          : "-"}
                      </div>
                    </div>
                    <div className="mypage-middle-content-info">
                      <div className="mypage-middle-content-subtitle">
                        결제 금액
                      </div>
                      <div className="mypage-middle-content-subcontent">
                        월 {storeData.planPrice?.toLocaleString()}원
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mypage-middle-content-down-m">
                <div className="mypage-middle-content-align-m2">
                  <div className="mypage-middle-content-title-m">히스토리</div>

                  <div className="mypage-credit-history">
                    <table className="mypage-credit-table">
                      <thead>
                        <tr>
                          <th>기간</th>
                          <th>구독플랜명</th>
                          <th>구독 유형</th>
                          <th>결제금액</th>
                          <th>결제일</th>
                        </tr>
                      </thead>

                      <tbody>
                        {storeData.membershipInfo &&
                        storeData.membershipInfo.length > 0 ? (
                          storeData.membershipInfo.map((membership, index) => (
                            <tr key={index}>
                              <td>
                                {formatDate(membership.MembershipStartDate)} ~{" "}
                                {formatDate(membership.MembershipEndDate)}
                              </td>
                              <td>{membership.PlanNm || "-"}</td>
                              <td>{membership.PlanType || "-"}</td>
                              <td>
                                ₩{membership.PlanPrice?.toLocaleString() || 0}
                              </td>
                              <td>{formatDate(membership.PaymentDateTime)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5">히스토리가 없습니다.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {cardActive && (
            <div className="mypage-card-content-wrap">
              <div className="mypage-card-content-down">
                <div className="mypage-card-content-align">
                  <div className="mypage-card-content-title">등록 정보</div>
                  <div className="mypage-card-history">
                    <table className="mypage-card-table">
                      <thead>
                        <tr>
                          <th>카드사</th>
                          <th>번호</th>
                          <th>유형</th>
                          <th>등록일</th>
                          <th>수정일</th>
                        </tr>
                      </thead>

                      <tbody>
                        {cardData ? (
                          <tr>
                            <td>{cardData.cardCompany || "-"}</td>
                            <td>{cardData.cardNumberMasked || "-"}</td>
                            <td>{cardData.cardType || "-"}</td>
                            <td>{formatDate(cardData.createdAt)}</td>
                            <td>{formatDate(cardData.updatedAt)}</td>
                          </tr>
                        ) : (
                          <tr>
                            <td colSpan="5">등록된 카드가 없습니다.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="mypage-card-button-group">
                    {cardData ? (
                      <>
                        <button
                          className="mypage-card-button"
                          onClick={handleCardEdit}
                        >
                          카드 수정
                        </button>
                        <button
                          className="mypage-card-button mypage-card-button--delete"
                          onClick={() => setShowCardDeleteConfirmModal(true)}
                        >
                          카드 삭제
                        </button>
                      </>
                    ) : (
                      <button
                        className="mypage-card-button"
                        onClick={handleCardRegister}
                      >
                        카드 등록
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 비밀번호 확인 모달 */}
      {showPasswordModal && (
        <WithdrawalPasswordModal
          onClose={() => setShowPasswordModal(false)}
          onPasswordVerified={() => {
            setShowPasswordModal(false);
            setShowConfirmModal(true);
          }}
          storeCode={storeData?.storeCode || 1}
        />
      )}

      {/* 탈퇴 확인 모달 */}
      {showConfirmModal && (
        <WithdrawalConfirmModal
          onClose={() => setShowConfirmModal(false)}
          storeCode={storeData?.storeCode || 1}
        />
      )}

      {/* 카드 등록/수정 성공 모달 */}
      {billingResult && (
        <div
          className="billing-modal-overlay"
          onClick={() => setBillingResult(null)}
        >
          <div
            className="billing-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="billing-modal-close"
              onClick={() => setBillingResult(null)}
            >
              ×
            </button>
            <div className="billing-modal-icon">
              {billingResult === "delete" ? "🗑️" : "✓"}
            </div>
            <div className="billing-modal-title">
              {billingResult === "edit" ? "카드 수정 완료" : billingResult === "delete" ? "카드 삭제 완료" : "카드 등록 완료"}
            </div>
            <div className="billing-modal-desc">
              {billingResult === "edit"
                ? "카드가 성공적으로 수정되었습니다."
                : billingResult === "delete"
                ? "카드가 성공적으로 삭제되었습니다."
                : "카드가 성공적으로 등록되었습니다."}
            </div>
            <button
              className="billing-modal-confirm-btn"
              onClick={() => setBillingResult(null)}
            >
              확인
            </button>
          </div>
        </div>
      )}

      

      {/* 카드 삭제 확인 모달 */}
      {showCardDeleteConfirmModal && (
        <div
          className="billing-modal-overlay"
          onClick={() => setShowCardDeleteConfirmModal(false)}
        >
          <div
            className="billing-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="billing-modal-close"
              onClick={() => setShowCardDeleteConfirmModal(false)}
            >
              ×
            </button>
            <div className="billing-modal-icon billing-modal-icon--fail">🗑️</div>
            <div className="billing-modal-title">카드 삭제</div>
            <div className="billing-modal-desc">
              카드를 삭제 하시겠습니까?
            </div>
            <div className="billing-modal-btn-group">
              <button
                className="billing-modal-confirm-btn billing-modal-confirm-btn-fail"
                onClick={handleCardDelete}
              >
                삭제
              </button>
              <button
                className="billing-modal-confirm-btn"
                onClick={() => setShowCardDeleteConfirmModal(false)}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 카드 등록 실패 모달 */}
      {showBillingFailModal && (
        <div
          className="billing-modal-overlay"
          onClick={() => setShowBillingFailModal(false)}
        >
          <div
            className="billing-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="billing-modal-close"
              onClick={() => setShowBillingFailModal(false)}
            >
              ×
            </button>
            <div className="billing-modal-icon billing-modal-icon--fail">✕</div>
            <div className="billing-modal-title">카드 등록 실패</div>
            <div className="billing-modal-desc">
              카드 등록 중 오류가 발생했습니다.
              <br />
              잠시 후 다시 시도해 주세요.
            </div>
            <button
              className="billing-modal-confirm-btn billing-modal-confirm-btn-fail"
              onClick={() => setShowBillingFailModal(false)}
            >
              확인
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPage;
